import { useState } from "react";
import { motion } from "framer-motion";
import { Loader2, CreditCard } from "lucide-react";
import { PublicLayout } from "@/components/PublicLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

function F({ label, children }) {
  return (
    <div className="space-y-1">
      <label className="text-sm font-semibold text-slate-700">{label}</label>
      {children}
    </div>
  );
}

export default function Membership() {
  const [form, setForm] = useState({ adult_name: "", adult_email: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.adult_name.trim()) { toast.error("Please enter your full name."); return; }
    if (!form.adult_email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.adult_email)) {
      toast.error("Please enter a valid email address."); return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/membership/checkout", {
        adult_name: form.adult_name.trim(),
        adult_email: form.adult_email.trim().toLowerCase(),
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(detail || "Something went wrong. Please try again.");
      setLoading(false);
    }
  };

  return (
    <PublicLayout>
      {/* Page — all white/light, no dark backgrounds */}
      <section className="bg-white px-4 py-14 sm:py-20">
        <div className="max-w-5xl mx-auto">

          {/* Heading */}
          <motion.div
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-primary uppercase">
              Membership Fee
            </h1>
            <p className="mt-4 text-lg text-slate-500 max-w-xl mx-auto">
              Pay your Fun-Nastix membership fee securely online.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12 items-start">

            {/* Left: info */}
            <motion.div
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
            >
              <h2 className="font-display text-3xl sm:text-4xl text-primary uppercase mb-6">
                Annual Membership
              </h2>

              {/* Price badge — white with primary border */}
              <div className="inline-block border-4 border-primary rounded-2xl px-8 py-5 mb-6">
                <p className="font-display text-5xl font-bold text-primary">£30.00</p>
                <p className="text-slate-500 text-sm mt-1">Payable annually, subject to change</p>
              </div>

              <p className="text-slate-600 text-base mb-4">
                A membership fee is required for all Fun-Nastix participants. It covers your child's
                registration for the current gymnastics year.
              </p>
              <p className="text-slate-600 text-base">
                If you're already booked on a class and need to pay your membership separately, simply
                complete the form and you'll be taken to our secure payment page.
              </p>
            </motion.div>

            {/* Right: form */}
            <motion.div
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="bg-slate-50 rounded-2xl border border-slate-200 p-8">
                <h3 className="font-display text-2xl text-primary mb-6">Pay Now</h3>
                <form onSubmit={submit} className="space-y-4">
                  <F label="Full Name">
                    <Input
                      value={form.adult_name}
                      onChange={set("adult_name")}
                      placeholder="Jane Smith"
                      className="rounded-xl"
                      data-testid="membership-name"
                    />
                  </F>
                  <F label="Email Address">
                    <Input
                      type="email"
                      value={form.adult_email}
                      onChange={set("adult_email")}
                      placeholder="jane@example.com"
                      className="rounded-xl"
                      data-testid="membership-email"
                    />
                  </F>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full rounded-full font-display text-lg py-6 bg-primary hover:bg-primary/90 mt-2"
                    data-testid="membership-pay-btn"
                  >
                    {loading
                      ? <><Loader2 size={18} className="animate-spin mr-2" /> Processing…</>
                      : <><CreditCard size={18} className="mr-2" /> Pay £30.00</>
                    }
                  </Button>
                  <p className="text-xs text-center text-slate-400 pt-1">
                    You'll be taken to our secure Stripe checkout page.
                  </p>
                </form>
              </div>
            </motion.div>

          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
